/**
 * A que fila vertical va cada persona.
 *
 * Se calculan DOS numeros distintos, porque sirven para cosas distintas y
 * mezclarlos daba un diagrama mal leido. Esta documentado con detalle, y con
 * las cuentas del arbol real, en docs/GENERACIONES.md.
 *
 *  - `linaje`: profundidad maxima por descendencia pura. Es la "generacion de
 *    linaje" y es la que se muestra en la ficha de cada persona.
 *
 *  - `fila`: la fila donde se DIBUJA. Parte de lo mismo, pero antes agrupa en
 *    una sola clase generacional a los hermanos de una familia y a las dos
 *    personas de una pareja. Sin ese paso, en el arbol real 15 de 34 parejas
 *    quedaban en filas distintas (la persona que entra por matrimonio y no
 *    tiene padres en el arbol caia arriba), y dos medios hermanos quedaban a
 *    dos filas de distancia.
 *
 * Invariantes que `fila` cumple y que no se negocian:
 *   1. el hijo siempre esta estrictamente por debajo de sus progenitores
 *   2. las dos personas de una pareja estan en la misma fila
 *   3. los hermanos y medios hermanos estan en la misma fila
 *
 * Cuando una union entre ramas hace imposible cumplir (2) o (3) sin romper
 * (1), gana (1): la union se rechaza y queda un aviso. La estructura manda.
 */
import type { Indice } from "./grafo.js";

export interface Generaciones {
  /** Fila de dibujo. 0 = la generacion mas vieja, que va arriba. */
  fila: Map<string, number>;
  /** Generacion de linaje: profundidad maxima por descendencia pura. */
  linaje: Map<string, number>;
  /** La fila mas alta que existe. */
  maxFila: number;
  /** Persona -> representante de su clase generacional. */
  clase: Map<string, string>;
  /** Uniones rechazadas y ciclos encontrados. No bloquean. */
  avisos: string[];
}

/** Union-find con compresion de caminos. */
class Conjuntos {
  private readonly padre = new Map<string, string>();

  constructor(elementos: Iterable<string>) {
    for (const e of elementos) this.padre.set(e, e);
  }

  raiz(x: string): string {
    let r = x;
    for (;;) {
      const p = this.padre.get(r);
      if (p === undefined || p === r) break;
      r = p;
    }
    // compresion
    let c = x;
    for (;;) {
      const p = this.padre.get(c);
      if (p === undefined || p === c) break;
      this.padre.set(c, r);
      c = p;
    }
    return r;
  }

  unir(a: string, b: string): void {
    const ra = this.raiz(a);
    const rb = this.raiz(b);
    if (ra !== rb) this.padre.set(ra, rb);
  }

  juntos(a: string, b: string): boolean {
    return this.raiz(a) === this.raiz(b);
  }
}

/**
 * Camino mas largo en un grafo aciclico dirigido.
 * Devuelve la profundidad de cada nodo y los nodos que quedaron en un ciclo.
 */
function caminoMasLargo(
  nodos: Iterable<string>,
  salientes: Map<string, string[]>,
): { nivel: Map<string, number>; enCiclo: string[] } {
  const nivel = new Map<string, number>();
  const entrantes = new Map<string, number>();
  for (const n of nodos) {
    nivel.set(n, 0);
    if (!entrantes.has(n)) entrantes.set(n, 0);
  }
  for (const [, destinos] of salientes) {
    for (const d of destinos) entrantes.set(d, (entrantes.get(d) ?? 0) + 1);
  }

  const cola: string[] = [];
  for (const [n, g] of entrantes) if (g === 0) cola.push(n);

  let visitados = 0;
  while (cola.length) {
    const n = cola.pop() as string;
    visitados++;
    for (const d of salientes.get(n) ?? []) {
      const cand = (nivel.get(n) ?? 0) + 1;
      if (cand > (nivel.get(d) ?? 0)) nivel.set(d, cand);
      const g = (entrantes.get(d) ?? 0) - 1;
      entrantes.set(d, g);
      if (g === 0) cola.push(d);
    }
  }

  const enCiclo: string[] = [];
  if (visitados < nivel.size) {
    for (const [n, g] of entrantes) if (g > 0) enCiclo.push(n);
  }
  return { nivel, enCiclo };
}

/** Las aristas progenitor -> hijo, a nivel de persona. */
function aristasPersona(ix: Indice): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const f of ix.arbol.familias.values()) {
    for (const p of [f.padre, f.madre]) {
      if (!p || !ix.arbol.personas.has(p)) continue;
      for (const ref of f.hijos) {
        if (ix.arbol.personas.has(ref.hlink)) out.push([p, ref.hlink]);
      }
    }
  }
  return out;
}

/** Agrupa las aristas por clase, descartando las que quedan dentro de una clase. */
function aristasClase(
  aristas: Array<[string, string]>,
  cj: Conjuntos,
): Map<string, string[]> {
  const vistas = new Set<string>();
  const salientes = new Map<string, string[]>();
  for (const [a, b] of aristas) {
    const ra = cj.raiz(a);
    const rb = cj.raiz(b);
    if (ra === rb) continue;
    const llave = ra + "\u0000" + rb;
    if (vistas.has(llave)) continue;
    vistas.add(llave);
    const ls = salientes.get(ra);
    if (ls) ls.push(rb);
    else salientes.set(ra, [rb]);
  }
  return salientes;
}

/** Hay camino de `desde` a `hasta` siguiendo las aristas de clase? */
function alcanzable(
  desde: string,
  hasta: string,
  salientes: Map<string, string[]>,
): boolean {
  if (desde === hasta) return true;
  const pila = [desde];
  const vistos = new Set<string>([desde]);
  while (pila.length) {
    const n = pila.pop() as string;
    for (const d of salientes.get(n) ?? []) {
      if (d === hasta) return true;
      if (!vistos.has(d)) {
        vistos.add(d);
        pila.push(d);
      }
    }
  }
  return false;
}

/**
 * Calcula filas y generaciones de linaje.
 *
 * `etiqueta` sirve solo para que los avisos se lean: se le pasa una funcion
 * que convierte un handle en algo legible.
 */
export function calcularGeneraciones(
  ix: Indice,
  etiqueta: (h: string) => string = (h) => h,
): Generaciones {
  const personas = ix.todas();
  const aristas = aristasPersona(ix);
  const avisos: string[] = [];

  // --- 1. generacion de linaje: por persona, sin agrupar nada -------------
  const salientesPersona = new Map<string, string[]>();
  for (const [a, b] of aristas) {
    const ls = salientesPersona.get(a);
    if (ls) ls.push(b);
    else salientesPersona.set(a, [b]);
  }
  const lin = caminoMasLargo(personas, salientesPersona);
  if (lin.enCiclo.length) {
    avisos.push(
      `hay ${lin.enCiclo.length} persona(s) en un ciclo de descendencia ` +
        `(alguien es antepasado de si mismo): ${lin.enCiclo
          .slice(0, 5)
          .map(etiqueta)
          .join(", ")}`,
    );
  }

  // --- 2. clases generacionales -------------------------------------------
  const cj = new Conjuntos(personas);

  /** Une dos personas si eso no crea un ciclo. Devuelve si lo hizo. */
  const unirSiSePuede = (a: string, b: string, motivo: string): boolean => {
    if (cj.juntos(a, b)) return true;
    const sal = aristasClase(aristas, cj);
    const ra = cj.raiz(a);
    const rb = cj.raiz(b);
    // Unirlas crearia un ciclo si una ya es ascendiente de la otra.
    if (alcanzable(ra, rb, sal) || alcanzable(rb, ra, sal)) {
      avisos.push(
        `no se pudo poner en la misma fila a ${etiqueta(a)} y ${etiqueta(b)} ` +
          `(${motivo}): una desciende de la otra, y que el hijo quede por ` +
          "debajo del padre manda sobre todo lo demas",
      );
      return false;
    }
    cj.unir(a, b);
    return true;
  };

  // Hermanos primero: es la agrupacion mas fuerte.
  for (const f of ix.arbol.familias.values()) {
    const hijos = f.hijos.map((r) => r.hlink).filter((h) => ix.arbol.personas.has(h));
    const primero = hijos[0];
    if (primero === undefined) continue;
    for (let i = 1; i < hijos.length; i++) {
      unirSiSePuede(primero, hijos[i] as string, `hermanos en ${f.id ?? f.handle}`);
    }
  }

  // Parejas despues.
  for (const f of ix.arbol.familias.values()) {
    const { padre, madre } = f;
    if (!padre || !madre) continue;
    if (!ix.arbol.personas.has(padre) || !ix.arbol.personas.has(madre)) continue;
    unirSiSePuede(padre, madre, `pareja en ${f.id ?? f.handle}`);
  }

  // --- 3. filas: camino mas largo sobre el grafo de clases ----------------
  const salientesClase = aristasClase(aristas, cj);
  const raices = new Set(personas.map((p) => cj.raiz(p)));
  const porClase = caminoMasLargo(raices, salientesClase);
  if (porClase.enCiclo.length) {
    avisos.push(
      `quedaron ${porClase.enCiclo.length} clase(s) generacional(es) en un ` +
        "ciclo; su fila puede no ser fiable",
    );
  }

  const fila = new Map<string, number>();
  const clase = new Map<string, string>();
  let maxFila = 0;
  for (const p of personas) {
    const r = cj.raiz(p);
    const n = porClase.nivel.get(r) ?? 0;
    fila.set(p, n);
    clase.set(p, r);
    if (n > maxFila) maxFila = n;
  }

  return { fila, linaje: lin.nivel, maxFila, clase, avisos };
}
